import React, { useState, useMemo, useEffect, useCallback } from 'react'
import {
    Box,
    Chip,
    CircularProgress,
    IconButton,
    makeStyles,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Theme,
    Tooltip,
    Typography,
} from '@material-ui/core'
import {
    CatalogFilterBar,
    CatalogFilterDrawer,
    CatalogSelectField,
    FilterChipItem,
    PropertyTagFilterField,
    getPropertyTagChip,
    uniqueNamedOptions,
} from '../../../ContractManager/CatalogFilterChrome'
import BusinessIcon from '@material-ui/icons/Business'
import VisibilityIcon from '@material-ui/icons/Visibility'
import DeleteIcon from '@material-ui/icons/Delete'
import RefreshIcon from '@material-ui/icons/Refresh'
import SendIcon from '@material-ui/icons/Send'
import DescriptionIcon from '@material-ui/icons/Description'
import { useOrgSampleCOIs, SampleCOI } from '../../../../hooks/useOrgSampleCOIs'
import { useUserTags } from '../../../../contexts/UserTagsContext'
import { getServiceImageDetails } from '../../../../models'
import { SampleCOIViewModal } from '../../../ContractManager/SampleCOIViewModal'
import { formatDate } from '../../../ContractManager/utils'
import { DeleteConfirmDialog } from '../../../ContractManager/DeleteConfirmDialog'
import { DropFiles } from '../../../../components/DropFiles/DropFiles'
import { useService } from '../../../../hooks/useService'
import { Company } from '../../../../models/Company/types'
import { SelectPropertiesUploadDialog } from './SelectPropertiesUploadDialog'
import { SendSampleToVendorsDialog } from '../../../ContractManager/SendSampleToVendorsDialog'
import { useOrganizationManagement } from '../../../../hooks/useOrganizationManagement'

const allowedFileTypes = {
    'application/pdf': ['.pdf'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
        '.docx',
    ],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
}

function propertyIdsOf(item: { apartment: number; apartment_ids?: number[] }) {
    return item.apartment_ids && item.apartment_ids.length > 0
        ? item.apartment_ids
        : [item.apartment]
}

function propertyLabelOf(item: {
    apartment_name: string
    apartment_names?: string[]
}) {
    if (item.apartment_names && item.apartment_names.length > 0) {
        return item.apartment_names.join(', ')
    }
    return item.apartment_name
}

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(4),
    },
    searchContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    searchField: {
        minWidth: 300,
    },
    tableWrapper: {
        borderRadius: theme.spacing(1.5),
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[1],
        overflow: 'hidden',
    },
    tableContainer: {
        maxHeight: 'calc(100vh - 550px)',
    },
    headerRow: {
        backgroundColor: theme.palette.grey[100],
    },
    headerCell: {
        fontWeight: 600,
        color: theme.palette.text.secondary,
        fontSize: '13px',
    },
    row: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    nameCell: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
    },
    documentIcon: {
        height: 32,
        width: 32,
        borderRadius: theme.spacing(0.5),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.primary.light + '20',
        color: theme.palette.primary.main,
    },
    serviceIconsContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        overflow: 'visible',
    },
    serviceIcon: {
        width: 24,
        height: 24,
        borderRadius: '50%',
        cursor: 'pointer',
        flexShrink: 0,
        objectFit: 'contain',
    },
    serviceColorDot: {
        width: 24,
        height: 24,
        borderRadius: '50%',
        flexShrink: 0,
    },
    moreServicesChip: {
        fontSize: '11px',
        height: 24,
        backgroundColor: theme.palette.grey[200],
        cursor: 'pointer',
    },
    summaryCell: {
        color: theme.palette.text.secondary,
        fontSize: '13px',
        minWidth: 200,
        maxWidth: 400,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    analyzingContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        color: theme.palette.primary.main,
    },
    analyzingText: {
        fontStyle: 'italic',
        fontSize: '13px',
    },
    actionsCell: {
        textAlign: 'right',
        whiteSpace: 'nowrap',
        width: '1%',
        minWidth: 140,
    },
    propertyCell: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    tagChip: {
        height: 20,
        fontSize: '11px',
        fontWeight: 500,
    },
    emptyRow: {
        textAlign: 'center',
        color: theme.palette.text.secondary,
        padding: theme.spacing(4),
    },
}))

interface OrganizationSampleCOIsProps {
    organization: number | null
    selectedTagId: number | null
    onSelectedTagIdChange: (value: number | null) => void
    propertyList: Company[]
}

export const OrganizationSampleCOIs: React.FC<OrganizationSampleCOIsProps> = ({
    organization,
    selectedTagId,
    onSelectedTagIdChange,
    propertyList,
}) => {
    const classes = useStyles()
    const { userTags } = useUserTags()

    const {
        sampleCOIs,
        isLoadingSampleCOIs,
        processSampleCOI,
        uploadSampleCOIs,
        updateSampleCOI,
        deleteSampleCOI,
        processingIds,
        sendToVendors,
        isUploading,
        isUpdating,
        isDeleting,
        isSending,
    } = useOrgSampleCOIs({ organization })

    const {
        vendorList,
        loadingState,
        getOrganizationVendors,
    } = useOrganizationManagement()

    useEffect(() => {
        if (organization) {
            getOrganizationVendors(organization)
        }
    }, [organization])

    const { serviceList } = useService({ getServiceList: true })

    // Search state
    const [searchText, setSearchText] = useState('')
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
    const [propertyFilter, setPropertyFilter] = useState<'all' | number>('all')
    const [serviceFilter, setServiceFilter] = useState<'all' | 'none' | number>(
        'all',
    )

    // Pagination state
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(25)

    // View modal state
    const [viewModalOpen, setViewModalOpen] = useState(false)
    const [selectedSample, setSelectedSample] = useState<SampleCOI | null>(null)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [sampleToDelete, setSampleToDelete] = useState<SampleCOI | null>(null)
    const [sampleToSend, setSampleToSend] = useState<SampleCOI | null>(null)
    const sendVendors = useMemo(() => {
        if (!sampleToSend) return []
        const propertyIds = new Set(propertyIdsOf(sampleToSend))
        return vendorList.filter((vendor) =>
            vendor.properties?.some((property) => propertyIds.has(property.id)),
        )
    }, [sampleToSend, vendorList])
    const [propertiesTarget, setPropertiesTarget] = useState<SampleCOI | null>(
        null,
    )
    const [pendingFiles, setPendingFiles] = useState<File[]>([])
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false)

    useEffect(() => {
        setPage(0)
    }, [selectedTagId, propertyFilter, serviceFilter])

    // Get all tags for a property (a property can have multiple tags)
    const getTagsForProperty = useCallback(
        (apartmentId: number) => {
            return userTags.filter((tag) =>
                tag.apartments.includes(apartmentId),
            )
        },
        [userTags],
    )

    const propertyOptions = useMemo(() => {
        const names = new Map<number, string>()
        sampleCOIs.forEach((coi) => {
            const ids = propertyIdsOf(coi)
            const labels =
                coi.apartment_names && coi.apartment_names.length > 0
                    ? coi.apartment_names
                    : [coi.apartment_name]
            ids.forEach((id, index) => {
                names.set(id, labels[index] || `Property ${id}`)
            })
        })
        return uniqueNamedOptions(
            Array.from(names.entries()).map(([id, name]) => ({ id, name })),
        )
    }, [sampleCOIs])
    const serviceOptions = useMemo(
        () =>
            uniqueNamedOptions(sampleCOIs.flatMap((coi) => coi.services || [])),
        [sampleCOIs],
    )

    const filteredCOIs = useMemo(() => {
        let filtered = sampleCOIs

        if (selectedTagId !== null) {
            const selectedTag = userTags.find((tag) => tag.id === selectedTagId)
            if (selectedTag) {
                filtered = filtered.filter((coi) =>
                    propertyIdsOf(coi).some((id) =>
                        selectedTag.apartments.includes(id),
                    ),
                )
            }
        }
        if (propertyFilter !== 'all') {
            filtered = filtered.filter((coi) =>
                propertyIdsOf(coi).includes(propertyFilter),
            )
        }
        if (serviceFilter === 'none') {
            filtered = filtered.filter((coi) => !coi.services?.length)
        } else if (serviceFilter !== 'all') {
            filtered = filtered.filter((coi) =>
                coi.services?.some((service) => service.id === serviceFilter),
            )
        }

        if (searchText.trim()) {
            const searchLower = searchText.toLowerCase()
            filtered = filtered.filter((coi) => {
                const nameMatch = coi.name.toLowerCase().includes(searchLower)
                const summaryMatch = coi.ai_summary
                    ?.toLowerCase()
                    .includes(searchLower)
                const apartmentMatch = propertyLabelOf(coi)
                    .toLowerCase()
                    .includes(searchLower)
                const serviceMatch = coi.services?.some((service) =>
                    service.name?.toLowerCase().includes(searchLower),
                )
                return (
                    nameMatch || summaryMatch || apartmentMatch || serviceMatch
                )
            })
        }

        return filtered
    }, [
        sampleCOIs,
        searchText,
        selectedTagId,
        userTags,
        propertyFilter,
        serviceFilter,
    ])

    const clearDrawerFilters = () => {
        setPropertyFilter('all')
        setServiceFilter('all')
        onSelectedTagIdChange(null)
        setPage(0)
    }

    // Paginate filtered COIs
    const paginatedCOIs = useMemo(() => {
        const start = page * rowsPerPage
        const end = start + rowsPerPage
        return filteredCOIs.slice(start, end)
    }, [filteredCOIs, page, rowsPerPage])

    const filterChips = useMemo(() => {
        const chips: FilterChipItem[] = []
        const tagChip = getPropertyTagChip(selectedTagId, userTags, () =>
            onSelectedTagIdChange(null),
        )
        if (tagChip) chips.push(tagChip)
        if (propertyFilter !== 'all') {
            chips.push({
                id: 'property',
                label: `Property: ${
                    propertyOptions.find((item) => item.id === propertyFilter)
                        ?.name || 'Property'
                }`,
                onDelete: () => setPropertyFilter('all'),
            })
        }
        if (serviceFilter === 'none') {
            chips.push({
                id: 'service',
                label: 'Service: None',
                onDelete: () => setServiceFilter('all'),
            })
        } else if (serviceFilter !== 'all') {
            chips.push({
                id: 'service',
                label: `Service: ${
                    serviceOptions.find((item) => item.id === serviceFilter)
                        ?.name || 'Service'
                }`,
                onDelete: () => setServiceFilter('all'),
            })
        }
        return chips
    }, [
        selectedTagId,
        userTags,
        onSelectedTagIdChange,
        propertyFilter,
        serviceFilter,
        propertyOptions,
        serviceOptions,
    ])

    const handleChangePage = (
        _event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const handleView = (sample: SampleCOI) => {
        setSelectedSample(sample)
        setViewModalOpen(true)
    }

    const handleDeleteClick = (sample: SampleCOI) => {
        setSampleToDelete(sample)
        setDeleteDialogOpen(true)
    }

    const handleConfirmDelete = () => {
        if (!sampleToDelete) return
        deleteSampleCOI.mutate(sampleToDelete.id, {
            onSuccess: () => {
                setDeleteDialogOpen(false)
                setSampleToDelete(null)
            },
        })
    }

    const handleReanalyze = (sample: SampleCOI) => {
        processSampleCOI.mutate(sample.id)
    }

    const handleUpdate = (
        id: number,
        data: Parameters<typeof updateSampleCOI.mutate>[0]['data'],
    ) => {
        updateSampleCOI.mutate(
            { id, data },
            {
                onSuccess: (response) => {
                    setSelectedSample(response.data)
                },
            },
        )
    }

    if (isLoadingSampleCOIs) {
        return (
            <Box className={classes.loadingContainer}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box className={classes.root}>
            <Box className={classes.header}>
                <Typography variant="h6">Sample COIs (Requirements)</Typography>
                <Typography variant="body2" color="textSecondary">
                    Upload a sample once and apply it to one or more properties.
                </Typography>
            </Box>

            <DropFiles
                onDrop={(files) => {
                    if (files.length === 0) return
                    setPendingFiles(files)
                    setUploadDialogOpen(true)
                }}
                displayString={
                    isUploading
                        ? 'Uploading...'
                        : 'Drag and drop sample COI files here'
                }
                fileTypes={allowedFileTypes}
                multipleFiles={true}
            />

            <CatalogFilterBar
                searchPlaceholder="Search sample COIs..."
                searchValue={searchText}
                onSearchChange={(value) => {
                    setSearchText(value)
                    setPage(0)
                }}
                onOpenFilters={() => setFilterDrawerOpen(true)}
                chips={filterChips}
                onClearAll={
                    filterChips.length > 0 ? clearDrawerFilters : undefined
                }
                resultLabel={`${filteredCOIs.length} sample COI${
                    filteredCOIs.length !== 1 ? 's' : ''
                }`}
            />
            <CatalogFilterDrawer
                open={filterDrawerOpen}
                onClose={() => setFilterDrawerOpen(false)}
                title="Sample COI filters"
                activeCount={filterChips.length}
                onClear={clearDrawerFilters}
            >
                <PropertyTagFilterField
                    value={selectedTagId}
                    onChange={(value) => {
                        onSelectedTagIdChange(value)
                        setPage(0)
                    }}
                />
                <CatalogSelectField
                    label="Property"
                    value={propertyFilter}
                    onChange={(value) =>
                        setPropertyFilter(
                            value === 'all' ? 'all' : Number(value),
                        )
                    }
                    options={[
                        { value: 'all', label: 'All properties' },
                        ...propertyOptions.map((property) => ({
                            value: property.id,
                            label: property.name,
                        })),
                    ]}
                />
                <CatalogSelectField
                    label="Service"
                    value={serviceFilter}
                    onChange={(value) =>
                        setServiceFilter(
                            value === 'all' || value === 'none'
                                ? value
                                : Number(value),
                        )
                    }
                    options={[
                        { value: 'all', label: 'All services' },
                        { value: 'none', label: 'No service' },
                        ...serviceOptions.map((service) => ({
                            value: service.id,
                            label: service.name,
                        })),
                    ]}
                />
            </CatalogFilterDrawer>

            {/* Table */}
            <Paper className={classes.tableWrapper}>
                <TableContainer className={classes.tableContainer}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow className={classes.headerRow}>
                                <TableCell className={classes.headerCell}>
                                    Requirement Name
                                </TableCell>
                                <TableCell className={classes.headerCell}>
                                    Property
                                </TableCell>
                                <TableCell className={classes.headerCell}>
                                    Services
                                </TableCell>
                                <TableCell className={classes.headerCell}>
                                    AI Summary
                                </TableCell>
                                <TableCell className={classes.headerCell}>
                                    Uploaded
                                </TableCell>
                                <TableCell
                                    className={`${classes.headerCell} ${classes.actionsCell}`}
                                    align="right"
                                >
                                    Actions
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedCOIs.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={6}
                                        className={classes.emptyRow}
                                    >
                                        {searchText
                                            ? 'No matching sample COIs found'
                                            : 'No sample COIs found'}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedCOIs.map((sample) => (
                                    <TableRow
                                        key={sample.id}
                                        className={classes.row}
                                        onClick={() => handleView(sample)}
                                    >
                                        <TableCell>
                                            <Box className={classes.nameCell}>
                                                <Box
                                                    className={
                                                        classes.documentIcon
                                                    }
                                                >
                                                    <DescriptionIcon fontSize="small" />
                                                </Box>
                                                <Typography variant="body2">
                                                    {sample.name}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box
                                                className={classes.propertyCell}
                                            >
                                                <span>
                                                    {propertyLabelOf(sample)}
                                                </span>
                                                {(() => {
                                                    const tags = propertyIdsOf(
                                                        sample,
                                                    ).flatMap((id) =>
                                                        getTagsForProperty(id),
                                                    )
                                                    const uniqueTags = tags.filter(
                                                        (tag, index) =>
                                                            tags.findIndex(
                                                                (other) =>
                                                                    other.id ===
                                                                    tag.id,
                                                            ) === index,
                                                    )
                                                    return uniqueTags.length >
                                                        0 ? (
                                                        <Box
                                                            display="flex"
                                                            flexWrap="wrap"
                                                            style={{ gap: 4 }}
                                                        >
                                                            {uniqueTags.map(
                                                                (tag) => (
                                                                    <Chip
                                                                        key={
                                                                            tag.id
                                                                        }
                                                                        label={
                                                                            tag.name
                                                                        }
                                                                        size="small"
                                                                        className={
                                                                            classes.tagChip
                                                                        }
                                                                        style={{
                                                                            backgroundColor:
                                                                                tag.color,
                                                                            color:
                                                                                'white',
                                                                        }}
                                                                    />
                                                                ),
                                                            )}
                                                        </Box>
                                                    ) : null
                                                })()}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box
                                                className={
                                                    classes.serviceIconsContainer
                                                }
                                            >
                                                {sample.services.length > 0 ? (
                                                    <>
                                                        {sample.services
                                                            .slice(0, 3)
                                                            .map((service) => {
                                                                const details = getServiceImageDetails(
                                                                    service.id,
                                                                    service.service_category,
                                                                )
                                                                return (
                                                                    <Tooltip
                                                                        key={
                                                                            service.id
                                                                        }
                                                                        title={
                                                                            service.name
                                                                        }
                                                                        arrow
                                                                    >
                                                                        {details?.icon ? (
                                                                            <img
                                                                                src={
                                                                                    details.icon
                                                                                }
                                                                                alt={
                                                                                    service.name
                                                                                }
                                                                                className={
                                                                                    classes.serviceIcon
                                                                                }
                                                                            />
                                                                        ) : (
                                                                            <Box
                                                                                className={
                                                                                    classes.serviceColorDot
                                                                                }
                                                                                style={{
                                                                                    backgroundColor:
                                                                                        service.color ||
                                                                                        '#9e9e9e',
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </Tooltip>
                                                                )
                                                            })}
                                                        {sample.services
                                                            .length > 3 && (
                                                            <Tooltip
                                                                title={sample.services
                                                                    .slice(3)
                                                                    .map(
                                                                        (s) =>
                                                                            s.name,
                                                                    )
                                                                    .join(', ')}
                                                                arrow
                                                            >
                                                                <Chip
                                                                    label={`+${
                                                                        sample
                                                                            .services
                                                                            .length -
                                                                        3
                                                                    }`}
                                                                    size="small"
                                                                    className={
                                                                        classes.moreServicesChip
                                                                    }
                                                                />
                                                            </Tooltip>
                                                        )}
                                                    </>
                                                ) : (
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                    >
                                                        —
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {processingIds.has(sample.id) ? (
                                                <Box
                                                    className={
                                                        classes.analyzingContainer
                                                    }
                                                >
                                                    <CircularProgress
                                                        size={14}
                                                        color="primary"
                                                    />
                                                    <Typography
                                                        className={
                                                            classes.analyzingText
                                                        }
                                                    >
                                                        Analyzing...
                                                    </Typography>
                                                </Box>
                                            ) : sample.ai_summary ? (
                                                <Tooltip
                                                    title={
                                                        <Typography
                                                            variant="body2"
                                                            style={{
                                                                whiteSpace:
                                                                    'pre-wrap',
                                                            }}
                                                        >
                                                            {sample.ai_summary}
                                                        </Typography>
                                                    }
                                                    arrow
                                                    placement="bottom-start"
                                                    interactive
                                                >
                                                    <Box
                                                        className={
                                                            classes.summaryCell
                                                        }
                                                    >
                                                        {sample.ai_summary}
                                                    </Box>
                                                </Tooltip>
                                            ) : (
                                                '—'
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                {formatDate(
                                                    sample.created_date,
                                                )}
                                            </Typography>
                                        </TableCell>
                                        <TableCell
                                            className={classes.actionsCell}
                                            onClick={(event) =>
                                                event.stopPropagation()
                                            }
                                        >
                                            <Tooltip title="Reanalyze">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            handleReanalyze(
                                                                sample,
                                                            )
                                                        }
                                                        disabled={processingIds.has(
                                                            sample.id,
                                                        )}
                                                    >
                                                        <RefreshIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title="Properties">
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        setPropertiesTarget(
                                                            sample,
                                                        )
                                                    }
                                                >
                                                    <BusinessIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Send to Vendors">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            setSampleToSend(
                                                                sample,
                                                            )
                                                        }
                                                        disabled={isSending}
                                                    >
                                                        <SendIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title="View">
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        handleView(sample)
                                                    }
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        handleDeleteClick(
                                                            sample,
                                                        )
                                                    }
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    count={filteredCOIs.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                />
            </Paper>

            {/* View Modal */}
            <SampleCOIViewModal
                isOpen={viewModalOpen}
                onClose={() => setViewModalOpen(false)}
                sampleCOI={selectedSample}
                onUpdate={handleUpdate}
                isUpdating={isUpdating}
                allServices={serviceList}
            />

            <SelectPropertiesUploadDialog
                open={uploadDialogOpen}
                onClose={() => {
                    setUploadDialogOpen(false)
                    setPendingFiles([])
                }}
                files={pendingFiles}
                propertyList={propertyList}
                title="Select properties for these sample COIs"
                submitLabel="Upload"
                isSubmitting={isUploading}
                onConfirm={async (propertyIds) => {
                    const results = await uploadSampleCOIs(
                        pendingFiles,
                        propertyIds,
                    )
                    results.forEach((result) => {
                        if (
                            result.status === 'fulfilled' &&
                            result.value?.data?.id
                        ) {
                            processSampleCOI.mutate(result.value.data.id)
                        }
                    })
                    setPendingFiles([])
                    setUploadDialogOpen(false)
                }}
            />

            <SelectPropertiesUploadDialog
                open={propertiesTarget != null}
                onClose={() => setPropertiesTarget(null)}
                files={[]}
                propertyList={propertyList}
                title={
                    propertiesTarget
                        ? `Properties for ${propertiesTarget.name}`
                        : 'Properties'
                }
                submitLabel="Save"
                submittingLabel="Saving..."
                initialSelectedIds={
                    propertiesTarget ? propertyIdsOf(propertiesTarget) : []
                }
                isSubmitting={isUpdating}
                onConfirm={(propertyIds) => {
                    if (!propertiesTarget) return
                    updateSampleCOI.mutate(
                        {
                            id: propertiesTarget.id,
                            data: { apartment_ids: propertyIds },
                        },
                        { onSuccess: () => setPropertiesTarget(null) },
                    )
                }}
            />

            <SendSampleToVendorsDialog
                open={sampleToSend != null}
                title="Send sample COI to vendors"
                documentName={sampleToSend?.name || ''}
                vendors={sendVendors}
                vendorsLoading={loadingState.vendorList}
                emptyMessage="No vendors on this sample's properties"
                isSending={isSending}
                onClose={() => setSampleToSend(null)}
                onSend={(vendorIds) => {
                    if (!sampleToSend) return
                    sendToVendors.mutate(
                        { id: sampleToSend.id, vendorIds },
                        { onSuccess: () => setSampleToSend(null) },
                    )
                }}
            />

            <DeleteConfirmDialog
                isOpen={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={handleConfirmDelete}
                documentName={sampleToDelete?.name || ''}
                isDeleting={isDeleting}
            />
        </Box>
    )
}
